// Shared Quick/Full pair collapsing, used by the navbar model-version
// dropdown (src/contexts/FilterContext/useModelVersions.ts) and the Settings
// Models tab (src/features/Settings/utils/modelPairs.ts). Each call site
// supplies its own identity accessors and survivor rule; the pairing
// algorithm itself lives here.

export type PairSurvivor = 'both' | 'quick' | 'full';

type CollapsePairsOptions<T> = {
  /** True for the Quick half of a pair. */
  isQuick: (item: T) => boolean;
  /** Identity shared by both halves of a pair (e.g. base model name). */
  pairKey: (item: T) => string;
  /**
   * Decides which half of a complete pair stays visible. Defaults to 'full'
   * (dropdown semantics: everything listed is published, so an existing Full
   * supersedes its Quick preview). Unpaired items are always kept.
   */
  resolve?: (quick: T, full: T) => PairSurvivor;
};

export const collapsePairs = <T>(
  items: T[],
  { isQuick, pairKey, resolve = () => 'full' }: CollapsePairsOptions<T>,
): T[] => {
  const quickByKey = new Map<string, T>();
  const fullByKey = new Map<string, T>();
  for (const item of items) {
    (isQuick(item) ? quickByKey : fullByKey).set(pairKey(item), item);
  }

  return items.filter((item) => {
    const key = pairKey(item);
    const quick = quickByKey.get(key);
    const full = fullByKey.get(key);
    if (quick === undefined || full === undefined) return true;
    const survivor = resolve(quick, full);
    if (survivor === 'both') return true;
    return isQuick(item) ? survivor === 'quick' : survivor === 'full';
  });
};
